import { getEcho, logPack, validatedListen, arrWrap } from '@/common';
import type { ZodTypeAny } from 'zod';
import { toValue, type MaybeRefOrGetter } from 'vue';
import type { Channel } from 'laravel-echo';

/**
 * Listen to zero, one, or many channels.
 */
export const useChannel = <Z extends ZodTypeAny>(
    channelName: MaybeRefOrGetter<(string | null | undefined)[] | string | undefined | null>,
    event: { event: string; schema: Z },
    handler: (data: Z['_output'] | undefined) => void,
    opts?: { joinMessage?: string; channelPrefix?: string },
) => {
    const echo = getEcho();
    const pack = logPack(opts?.channelPrefix ?? `useChannel(${event.event}): `);
    const channels = new Map<string, Channel>();

    const leaveChannel = (channelToLeave: string | null | undefined) => {
        if (channelToLeave != null && channels.has(channelToLeave)) {
            echo?.leave(channelToLeave);
            pack.info(`Left channel: ${channelToLeave}`);
            channels.delete(channelToLeave);
        }
    };

    const joinChannel = (channelToJoin: string | null | undefined) => {
        if (channelToJoin != null && channels.has(channelToJoin)) {
            //nothing to do
            return;
        }
        leaveChannel(channelToJoin);
        if (channelToJoin != null) {
            const newChannel = echo?.private(channelToJoin);
            if (newChannel == null) {
                return;
            }
            console.log('Trying to join channel', channelToJoin);
            newChannel.subscribed(() => pack.success(`Joined channel: ${channelToJoin}`, opts?.joinMessage ?? ''));
            validatedListen(newChannel, event, handler);
            channels.set(channelToJoin, newChannel);
        }
    };

    watch(
        toRef(channelName),
        (newChannelNames, oldChannelNames) => {
            const o = arrWrap(oldChannelNames);
            const n = arrWrap(newChannelNames);
            const toLeave = o.filter(c => c && !n.includes(c));
            const toJoin = n.filter(c => c && !o.includes(c));
            toLeave.forEach(leaveChannel);
            toJoin.forEach(joinChannel);
        },
        { immediate: true },
    );

    onUnmounted(() => {
        arrWrap(toValue(channelName)).forEach(leaveChannel);
    });

    return { channels };
};
